import React from 'react';

import { StatusBadge } from 'components/pages/certificates/StatusBadge';
import { getCertificateStatusChip } from 'utils/displayStatusColorMapper';

interface CertificateStatusChipProps {
  /** Raw certificate status, e.g. `transfer_waiting_confirmation`. */
  status?: string | null;
  className?: string;
  /**
   * Passed through to `StatusBadge`. Every live call site now takes the DEFAULT
   * chip.
   *
   * ⚠️ This used to read "the Activity feed passes `dot`: every other surface in
   * the app now states status as a dot" — which VRG-511 had already made false
   * when it moved the certificates card to a tinted chip on measured grounds (the
   * dot renders at 1.21-1.46:1 on white, so its colour channel carries nothing).
   * The sentence then argued, from a world that no longer existed, for keeping the
   * one surface that had not been converted.
   */
  variant?: 'chip' | 'dot';
}

/**
 * The certificate-status pill (VERA-978).
 *
 * Deliberately NOT a new pill: it renders `StatusBadge`, the same component the
 * certificate header and the certificates list already use, so the chip in the
 * Activity timeline is the same object as the chip at the top of the page. All this
 * adds is the binding between a raw status and its label + tint, which lives in
 * `getCertificateStatusChip` (working conventions §28/§36 — reuse the pill, keep the
 * palette in one resolver rather than hand-computing `colors` at each call site).
 *
 * Renders NOTHING when the status is missing or unrecognised. Action-log rows only
 * sometimes record `snapshot.status`, so "no status" is the common case and must read
 * as silence, not as an "Unknown" chip.
 */
export const CertificateStatusChip: React.FC<CertificateStatusChipProps> = ({
  status,
  className,
  variant,
}) => {
  const meta = getCertificateStatusChip(status);
  if (!meta) return null;

  return (
    <StatusBadge
      className={className}
      status={status as string}
      label={meta.label}
      colors={meta.colors}
      variant={variant}
    />
  );
};

export default CertificateStatusChip;
